import { useState } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";

import {
  useFloating,
  useClick,
  useInteractions,
  autoUpdate,
  shift,
  useRole,
  useDismiss,
  FloatingPortal,
  offset,
  flip,
  FloatingFocusManager,
} from "@floating-ui/react";
import { Button } from "components/LibraryComponents/Components";

// store
import useAuthStore from "store/useAuthStore";

// styles
import styles from "./UnAuthenticatedMenu.module.scss";
import { Alert } from "@trussworks/react-uswds";

export default function UnAuthenticatedMenu() {
  const { loginOkta } = useAuthStore();

  const [open, setOpen] = useState<boolean>(false);

  const { refs, floatingStyles, context } = useFloating({
    open: open,
    onOpenChange: setOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { bubbles: true });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  const oktaSignIn = () => {
    // Navigate to /_api/auth/okta/login
    loginOkta();
  };
  const govSignIn = () => {
    // Redirects to the active gov login provider (MAX.gov or FedHub)
    window.location.href = "/_api/auth/gov/login";
  };

  return (
    <>
      <button
        type="button"
        className={styles["unauth-btn"]}
        aria-label="Sign in"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        Sign in
      </button>

      <FloatingPortal id="unauth-menu-popover">
        <FloatingFocusManager
          context={context}
          modal={false}
          // prevent the first item from auto-focusing on open
          initialFocus={-1}
          guards={false}
        >
          {open && (
            <div
              ref={refs.setFloating}
              style={{ ...floatingStyles, outline: "none" }}
              {...getFloatingProps()}
              className={styles["unauth-menu-content"]}
            >
              <p className={styles["heading"]}>Sign in</p>

              <p className={cn(styles["desc"], styles["mt-4"])}>
                Create an account and log in to claim ownership of your small
                business and update your business profile information.
              </p>

              <Button
                type="button"
                aria-label="Log in as a business owner"
                className={styles["mt-8"]}
                onClick={oktaSignIn}
              >
                Log in as a business owner
              </Button>

              <hr className={styles["divider"]} />

              <p className={styles["heading"]}>Federal government users</p>

              <p className={cn(styles["desc"], styles["mt-4"])}>
                If you’re a federal government employee, log in with your
                government account to see more information about businesses
                when searching.
              </p>
              <Alert type="info" headingLevel="h3" className={styles["gov-login-alert"]}>
                <p className={styles["alert-heading"]}><strong>Important note for government users:</strong></p>
                <p className={styles["alert-desc"]}>Effective April 11, 2026, all government user logins will use FedHub instead of Max.gov. You will be required to register your account when you first log in through FedHub, after which you can continue to access your SBS account. <a href="https://sbaone.atlassian.net/wiki/external/ODFjM2IxZDg4NDU0NDc2MDk5NDQ0MjI0OTVhM2RiZGQ" rel="noopener noreferrer" target="_blank">Click here to learn more</a>.</p>
              </Alert>
              <Button
                type="button"
                aria-label="Log in as a government user"
                className={styles["mt-8"]}
                onClick={govSignIn}
              >
                Log in as a government user
              </Button>
            </div>
          )}
        </FloatingFocusManager>
      </FloatingPortal>
    </>
  );
}
